/**
 * Curated 8-swatch brand-accent palette.
 *
 * Each swatch defines space-separated RGB triples (matching the format in
 * `src/design-system/tokens.css`) for the four accent CSS variables, with
 * separate `dark` and `light` blocks. All combinations have been WCAG-AA
 * validated against their respective `--bg-base`.
 *
 * Tenant admins pick a swatch via the BrandAccentPicker (Agent N). The
 * ThemeContext applies it at runtime by overriding the four CSS vars on
 * <html>, layered on top of whatever `tokens.css` declares for the
 * resolved theme.
 */

export type BrandAccent =
  | 'red'
  | 'blue'
  | 'emerald'
  | 'amber'
  | 'violet'
  | 'rose'
  | 'teal'
  | 'slate';

export interface SwatchTokens {
  /** RGB triple, space-separated, e.g. "220 38 38". */
  accent: string;
  accentHover: string;
  accentFg: string;
  ring: string;
}

export interface SwatchDef {
  key: BrandAccent;
  /** Human-readable label shown in the picker. */
  label: string;
  /** Tooltip / description for the picker. */
  description: string;
  /** Quick preview hex for the picker chip — uses the bare `accent` from dark mode. */
  previewHex: string;
  dark: SwatchTokens;
  light: SwatchTokens;
}

export const BRAND_SWATCHES: Record<BrandAccent, SwatchDef> = {
  red: {
    key: 'red',
    label: 'GOP Red',
    description: 'Republican / GOP campaigns. The default.',
    previewHex: '#dc2626',
    dark: {
      accent: '220 38 38',
      accentHover: '185 28 28',
      accentFg: '248 113 113',
      ring: '248 113 113',
    },
    light: {
      accent: '220 38 38',
      accentHover: '185 28 28',
      accentFg: '185 28 28',
      ring: '220 38 38',
    },
  },
  blue: {
    key: 'blue',
    label: 'Democratic Blue',
    description: 'Democratic campaigns / civic orgs.',
    previewHex: '#2563eb',
    dark: {
      accent: '37 99 235',
      accentHover: '29 78 216',
      accentFg: '96 165 250',
      ring: '96 165 250',
    },
    light: {
      accent: '37 99 235',
      accentHover: '29 78 216',
      accentFg: '29 78 216',
      ring: '37 99 235',
    },
  },
  emerald: {
    key: 'emerald',
    label: 'Independent Emerald',
    description: 'Independent / NGO / advocacy.',
    previewHex: '#059669',
    dark: {
      accent: '5 150 105',
      accentHover: '4 120 87',
      accentFg: '52 211 153',
      ring: '52 211 153',
    },
    light: {
      accent: '5 150 105',
      accentHover: '4 120 87',
      accentFg: '4 120 87',
      ring: '5 150 105',
    },
  },
  amber: {
    key: 'amber',
    label: 'Libertarian Amber',
    description: 'Libertarian / civic / community-org tone.',
    previewHex: '#d97706',
    dark: {
      accent: '217 119 6',
      accentHover: '180 83 9',
      accentFg: '251 191 36',
      ring: '251 191 36',
    },
    light: {
      accent: '217 119 6',
      accentHover: '180 83 9',
      accentFg: '180 83 9',
      ring: '217 119 6',
    },
  },
  violet: {
    key: 'violet',
    label: 'Tech Violet',
    description: 'Tech / data-org / modern brand.',
    previewHex: '#7c3aed',
    dark: {
      accent: '124 58 237',
      accentHover: '109 40 217',
      accentFg: '167 139 250',
      ring: '167 139 250',
    },
    light: {
      accent: '124 58 237',
      accentHover: '91 33 182',
      accentFg: '91 33 182',
      ring: '124 58 237',
    },
  },
  rose: {
    key: 'rose',
    label: 'Advocacy Rose',
    description: 'Advocacy / campaign / movement.',
    previewHex: '#e11d48',
    dark: {
      accent: '225 29 72',
      accentHover: '190 18 60',
      accentFg: '251 113 133',
      ring: '251 113 133',
    },
    light: {
      accent: '225 29 72',
      accentHover: '159 18 57',
      accentFg: '159 18 57',
      ring: '225 29 72',
    },
  },
  teal: {
    key: 'teal',
    label: 'Coalition Teal',
    description: 'Coalition / cross-partisan / nonprofit.',
    previewHex: '#0891b2',
    dark: {
      accent: '8 145 178',
      accentHover: '14 116 144',
      accentFg: '34 211 238',
      ring: '34 211 238',
    },
    light: {
      accent: '8 145 178',
      accentHover: '14 116 144',
      accentFg: '14 116 144',
      ring: '8 145 178',
    },
  },
  slate: {
    key: 'slate',
    label: 'Neutral Slate',
    description: 'Neutral / training / dev environment.',
    previewHex: '#475569',
    dark: {
      accent: '71 85 105',
      accentHover: '51 65 85',
      accentFg: '148 163 184',
      ring: '148 163 184',
    },
    light: {
      accent: '71 85 105',
      accentHover: '51 65 85',
      accentFg: '51 65 85',
      ring: '71 85 105',
    },
  },
};

/** Stable ordered list of swatch keys for picker UIs. */
export const BRAND_SWATCH_KEYS: BrandAccent[] = [
  'red',
  'blue',
  'emerald',
  'amber',
  'violet',
  'rose',
  'teal',
  'slate',
];

/**
 * Apply a brand accent to <html> by overriding the four CSS vars at runtime.
 * Picks `light` or `dark` block based on the current resolved theme.
 */
export function applyBrandAccent(
  accent: BrandAccent,
  resolved: 'light' | 'dark'
): void {
  if (typeof document === 'undefined') return;
  const swatch = BRAND_SWATCHES[accent];
  if (!swatch) return;
  const tokens = resolved === 'light' ? swatch.light : swatch.dark;
  const html = document.documentElement;
  html.style.setProperty('--accent', tokens.accent);
  html.style.setProperty('--accent-hover', tokens.accentHover);
  html.style.setProperty('--accent-fg', tokens.accentFg);
  html.style.setProperty('--ring', tokens.ring);
}

/**
 * Remove brand-accent overrides — falls back to whatever tokens.css declares
 * for the current `data-theme`.
 */
export function clearBrandAccent(): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  html.style.removeProperty('--accent');
  html.style.removeProperty('--accent-hover');
  html.style.removeProperty('--accent-fg');
  html.style.removeProperty('--ring');
}
